import { BlobstoreAssetRef, FileKind, ModelAsset } from 'web';
import { v4 as uuidv4 } from 'uuid';
import { validateFile } from '@/utils/nodeInputValidation';
import { log } from '@/logger/logger';

export type AssetType = (MediaAsset | TextAsset)['type'];

const logger = log.getLogger('assets');

export interface ImageRef {
  url: string;
  key?: string;
}

/** One asset's freshly resolved Blobstore URLs. Response-scoped: never persisted, never in the graph. */
export interface ResolvedAsset {
  type?: 'image' | 'video' | 'audio' | '3D';
  /** Asset-reference creation time; absent in responses from older servers. */
  createdAt?: string;
  signedUrl: string;
  signedThumbnailUrl?: string; // Resolved async after the thumbnail finished transcoding.
  signedWebpUrl?: string; // Images only; resolved async after the webp job finished.
}

/** Resolved URLs for one response, keyed by Blobstore asset id. */
export type ResolvedAssets = Record<string, ResolvedAsset>;

/** Upload response: the asset plus its response-scoped signed URLs. Never merge these into the asset. */
export interface UploadAssetResponse {
  asset: MediaAsset;
  resolvedAssets?: ResolvedAssets;
}

export type TextAsset = {
  id: string;
  type: 'text';
  input?: Record<string, string | number>;
  params?: Record<string, string | number>;
  value: string;
  originalValue?: string;
};

export type MediaAsset = {
  id: string;
  blobstore?: BlobstoreAssetRef;
  duration?: number;
  fps?: number;
  hasAudio?: boolean;
  height?: number;
  input?: Record<string, any>;
  mime?: string; // Blobstore-resolved media type; absent for Cloudinary-only assets.
  sizeBytes?: number; // Blobstore original byte count; absent for Cloudinary-only assets.
  name?: string;
  params?: Record<string, any>;
  publicId: string;
  thumbnailUrl: string;
  type: 'image' | 'video' | 'audio' | '3D';
  url: string;
  viewUrl: string;
  visualId: string;
  width?: number;
};

export type RenderingAsset = {
  type: 'rendering';
  progress?: number;
};

export type UploadedAsset = (TextAsset | MediaAsset | RenderingAsset) & {
  name?: string; // temp for design app output
  batchId?: string;
  order?: number;
  secondaryOrder?: number;
};

/** `generations[].input` is `Vec<(String, String)>` in Rust, so a reference has to travel as a string. */
const assetInputJson = (value: unknown): unknown => {
  if (!value || typeof value !== 'object' || !('blobstore' in value)) {
    return value;
  }

  const { url, blobstore, type } = value as { url?: unknown; blobstore?: { id?: unknown }; type?: unknown };

  return typeof blobstore?.id === 'string' ? JSON.stringify({ url, blobstore, type }) : value;
};

const validateInputInfo = (input: Record<string, any>) => {
  const array: [string, string][] = Array.from(
    Object.entries(input).map(([key, value]) => {
      if (value !== undefined && value !== null && typeof value === 'object' && 'input' in value) {
        value = value.input;
      }
      if (value !== undefined && value !== null && typeof value === 'object' && 'input_file' in value) {
        value = value.input_file;
      }
      if (value !== undefined && value !== null && typeof value === 'object' && 'image' in value) {
        value = value.image;
      }
      value = assetInputJson(value);
      if (value !== undefined && value !== null && Array.isArray(value)) {
        const items = value.map(assetInputJson);
        if (items.every((item) => typeof item === 'string')) {
          return [key, items.filter((item) => item !== '').join('\n')];
        }
        return [key, JSON.stringify(items)];
      }
      if (value === undefined || value === null) {
        return [key, ''];
      }
      if (typeof value === 'object') {
        if ('url' in value && typeof value.url === 'string') {
          return [key, value.url];
        }
        if ('seed' in value && typeof value.seed !== 'object') {
          return [key, value.seed.toString()];
        }
        if ('value' in value && typeof value.value !== 'object') {
          return [key, value.value.toString()];
        }
        if ('prompt' in value && typeof value.prompt !== 'object') {
          return [key, value.prompt.toString()];
        }
        if (
          'prompt' in value &&
          typeof value.prompt === 'object' &&
          value.prompt !== null &&
          value.prompt !== undefined &&
          'value' in value.prompt &&
          typeof value.prompt.value === 'string'
        ) {
          return [key, value.prompt.value];
        }
        if (
          'width' in value &&
          typeof value.width === 'number' &&
          'height' in value &&
          typeof value.height === 'number'
        ) {
          return [key, `${value.width}x${value.height}`];
        }
        // nosemgrep: figma.dont-log-filekeys -- `key` is a tool input name, not a Figma file key.
        logger.error(`Unexpected input value for key: ${key}, value: ${JSON.stringify(value)}`);
      }
      return [key, value.toString()];
    }),
  );
  return array;
};

/**
 * @deprecated Use the `ModelAsset` type directly instead of the `UploadedAsset` type.
 */
export function UploadedAssetToModelAsset(asset: UploadedAsset): ModelAsset | undefined {
  switch (asset.type) {
    case 'text': {
      return {
        name: asset.name,
        batchId: asset.batchId,
        order: asset.order,
        secondaryOrder: asset.secondaryOrder,
        input: validateInputInfo(asset.input ?? {}),
        parameters: validateInputInfo(asset.params ?? {}),
        kind: { type: 'text', value: asset.value, originalValue: asset.originalValue, id: asset.id ?? uuidv4() },
      };
    }
    case 'image':
    case 'video':
    case 'audio':
    case '3D': {
      const mediaAsset = asset as MediaAsset;
      const kind: FileKind | undefined = validateFile(mediaAsset);
      if (!kind) {
        logger.error(`Invalid media asset: ${mediaAsset.type}`);
        return undefined;
      }

      return {
        name: asset.name,
        batchId: asset.batchId,
        order: asset.order,
        secondaryOrder: asset.secondaryOrder,
        input: validateInputInfo(asset.input ?? {}),
        parameters: validateInputInfo(asset.params ?? {}),
        kind,
      };
    }
    default:
      logger.error(`Unsupported asset type: ${asset.type}`);
  }
}

/**
 * Validates a media {@link ModelAsset} and returns a {@link FileKind} (e.g. for import nodes).
 * Text generations are not representable as files and return `undefined`.
 */
export function fileKindFromModelAsset(asset: ModelAsset): FileKind | undefined {
  if (asset.kind.type === 'text') {
    return undefined;
  }
  return validateFile({ ...asset.kind, name: asset.name });
}

/**
 * @deprecated Use the `ModelAsset` type directly instead of the `UploadedAsset` type.
 */
export function ModelAssetToUploadedAsset(asset: ModelAsset): UploadedAsset {
  const common = {
    id: asset.kind.id ?? '',
    name: asset.name,
    batchId: asset.batchId,
    order: asset.order,
    secondaryOrder: asset.secondaryOrder,
    input: Object.fromEntries(asset.input.map((v) => [v[0], v[1].toString()])),
    params: Object.fromEntries(asset.parameters.map((v) => [v[0], v[1].toString()])),
  };

  switch (asset.kind.type) {
    case 'text':
      return {
        type: 'text',
        value: asset.kind.value,
        originalValue: asset.kind.originalValue,
        ...common,
      };
    case 'image':
      return {
        type: 'image',
        url: asset.kind.url,
        publicId: asset.kind.publicId ?? '',
        thumbnailUrl: asset.kind.thumbnailUrl ?? '',
        viewUrl: '',
        visualId: '',
        width: asset.kind.width,
        height: asset.kind.height,
        blobstore: asset.kind.blobstore,
        mime: asset.kind.mime,
        sizeBytes: asset.kind.sizeBytes,
        ...common,
      };
    case 'video':
      return {
        type: 'video',
        url: asset.kind.url,
        publicId: asset.kind.publicId ?? '',
        thumbnailUrl: asset.kind.thumbnailUrl ?? '',
        viewUrl: '',
        visualId: '',
        width: asset.kind.width,
        height: asset.kind.height,
        duration: asset.kind.duration,
        fps: asset.kind.fps,
        blobstore: asset.kind.blobstore,
        mime: asset.kind.mime,
        sizeBytes: asset.kind.sizeBytes,
        ...common,
      };
    case 'audio':
      return {
        type: 'audio',
        url: asset.kind.url,
        publicId: asset.kind.publicId ?? '',
        duration: asset.kind.duration,
        thumbnailUrl: '',
        viewUrl: '',
        visualId: '',
        blobstore: asset.kind.blobstore,
        mime: asset.kind.mime,
        sizeBytes: asset.kind.sizeBytes,
        ...common,
      };
    case '3D':
      return {
        type: '3D',
        url: asset.kind.url,
        publicId: asset.kind.publicId ?? '',
        thumbnailUrl: asset.kind.thumbnailUrl ?? '',
        viewUrl: '',
        visualId: '',
        blobstore: asset.kind.blobstore,
        mime: asset.kind.mime,
        sizeBytes: asset.kind.sizeBytes,
        ...common,
      };
  }
}
